import type { Entity, Folder } from './api.js';

/**
 * **Le cache local**, et le curseur qui le tient à jour.
 *
 * D3 dit que le web n'a pas d'index local, et c'est toujours vrai : ceci n'est pas
 * un index. C'est un **miroir** de ce que `GET /changes` a déjà livré, dont la
 * seule fonction est de peindre l'écran avant le premier aller-retour réseau — et
 * de le peindre encore quand il n'y en a pas.
 *
 * IndexedDB plutôt que `localStorage`, pour une raison précise : les jetons vivent
 * déjà dans `localStorage` (D37), et un cache qui grossit dans le même quota
 * finirait par faire évincer la session. Une déconnexion inexpliquée est le pire
 * symptôme qu'un cache puisse produire.
 *
 * **Le cache est cloisonné par `uid`.** Deux comptes sur le même navigateur ne
 * partagent rien : le nom de la base porte l'identifiant, et changer de compte ne
 * peut donc pas faire hériter du miroir de l'autre.
 */

/**
 * **2 depuis 4.6, et le passage de 1 à 2 efface le curseur** — c'est ainsi que se
 * ferme **D-23**.
 *
 * Jusqu'ici le drainage écartait `membership` et `edge` en **avançant quand même
 * le curseur** : ce qui est partagé avec nous a défilé sans être écrit, et ne sera
 * jamais reproposé à un client déjà synchronisé. Créer les deux magasins ne suffit
 * donc pas — ils resteraient vides pour tous ceux qui ont déjà un curseur, c'est-à-
 * dire pour tout le monde.
 *
 * La reprise **n'est pas un correctif, c'est un premier plein** : sans curseur, le
 * serveur rend `full: true` et le miroir est remplacé au lieu d'être fusionné. La
 * migration de schéma est le seul endroit où ce raisonnement peut être tenu une
 * fois pour toutes, plutôt qu'à chaque ouverture.
 */
const VERSION = 2;

const STORE_ENTITIES = 'entities';
const STORE_FOLDERS = 'folders';
const STORE_EDGES = 'edges';
const STORE_MEMBERSHIPS = 'memberships';
const STORE_META = 'meta';

const KEY_CURSOR = 'cursor';

/**
 * Une arête reçue, telle que `/changes` la livre : la projection d'un objet qu'on
 * ne possède pas, dans la partition d'un groupe. Elle n'est **pas** une entité —
 * `RxnAgg` y remplace `reactions`, `Folder` n'y a jamais été écrit — et les
 * confondre ferait apparaître dans « Mon espace » des objets qui ne sont pas à
 * nous.
 */
export interface CachedEdge {
  readonly container: string;
  readonly [key: string]: unknown;
}

export interface CachedMembership {
  readonly Gid: string;
  readonly GroupName?: string;
  readonly MemberRole?: string;
  readonly [key: string]: unknown;
}

export interface Cached {
  readonly entities: Entity[];
  readonly folders: Folder[];
  readonly edges: CachedEdge[];
  readonly memberships: CachedMembership[];
  readonly cursor?: string;
}

function open(uid: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(`synkara.${uid}`, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      // `Fid` ou `Aid` selon la nature ; la clé est calculée à l'écriture plutôt
      // que déclarée, parce qu'aucun `keyPath` unique ne couvre les deux.
      if (!db.objectStoreNames.contains(STORE_ENTITIES)) db.createObjectStore(STORE_ENTITIES);
      if (!db.objectStoreNames.contains(STORE_FOLDERS)) db.createObjectStore(STORE_FOLDERS);
      if (!db.objectStoreNames.contains(STORE_EDGES)) db.createObjectStore(STORE_EDGES);
      if (!db.objectStoreNames.contains(STORE_MEMBERSHIPS)) {
        db.createObjectStore(STORE_MEMBERSHIPS);
      }
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META);
      // **Le curseur tombe avec l'ancien schéma.** Le garder laisserait les deux
      // magasins neufs vides pour toujours : les pages qui portaient les arêtes
      // sont derrière lui, et rien ne les repropose.
      if (req.transaction) req.transaction.objectStore(STORE_META).delete(KEY_CURSOR);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB indisponible'));
  });
}

const done = (tx: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('transaction annulée'));
  });

const all = <T>(store: IDBObjectStore): Promise<T[]> =>
  new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result as T[]);
    req.onerror = () => reject(req.error);
  });

const one = <T>(store: IDBObjectStore, key: string): Promise<T | undefined> =>
  new Promise((resolve, reject) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });

/**
 * Le cache, ou rien.
 *
 * Un navigateur peut refuser IndexedDB — navigation privée, quota, réglage. Ce
 * n'est **pas** une panne de l'application : elle repart alors du réseau, plus
 * lentement et sans hors-ligne. Un cache est une accélération, jamais une
 * dépendance, et le traiter autrement transformerait une gêne en écran d'erreur.
 */
export class Cache {
  private constructor(private readonly db: IDBDatabase) {}

  static async open(uid: string): Promise<Cache | undefined> {
    try {
      return new Cache(await open(uid));
    } catch {
      return undefined;
    }
  }

  async read(): Promise<Cached> {
    const tx = this.db.transaction(
      [STORE_ENTITIES, STORE_FOLDERS, STORE_EDGES, STORE_MEMBERSHIPS, STORE_META],
      'readonly',
    );
    const [entities, folders, edges, memberships, cursor] = await Promise.all([
      all<Entity>(tx.objectStore(STORE_ENTITIES)),
      all<Folder>(tx.objectStore(STORE_FOLDERS)),
      all<CachedEdge>(tx.objectStore(STORE_EDGES)),
      all<CachedMembership>(tx.objectStore(STORE_MEMBERSHIPS)),
      one<string>(tx.objectStore(STORE_META), KEY_CURSOR),
    ]);
    return { entities, folders, edges, memberships, cursor };
  }

  /**
   * Applique une page de `/changes`, **puis** avance le curseur — dans la même
   * transaction et dans cet ordre.
   *
   * C'est la **règle d'avancement non régressive** de la cause R7, et l'ordre est
   * tout : un curseur avancé avant l'application laisserait, à la première
   * défaillance d'écriture, un trou définitif — les changements de l'intervalle
   * ne seront jamais reproposés. Ici, une transaction qui échoue laisse le
   * curseur où il était, et la page suivante recommence la même.
   */
  async apply(page: {
    entities: { key: string; value: Entity | null }[];
    folders: { key: string; value: Folder | null }[];
    edges: { key: string; value: CachedEdge | null }[];
    memberships: { key: string; value: CachedMembership | null }[];
    /** Les groupes quittés : **leurs arêtes partent avec** (purge locale, D17). */
    dropped: readonly string[];
    cursor: string;
    full: boolean;
  }): Promise<void> {
    const tx = this.db.transaction(
      [STORE_ENTITIES, STORE_FOLDERS, STORE_EDGES, STORE_MEMBERSHIPS, STORE_META],
      'readwrite',
    );
    const entities = tx.objectStore(STORE_ENTITIES);
    const folders = tx.objectStore(STORE_FOLDERS);
    const edges = tx.objectStore(STORE_EDGES);
    const memberships = tx.objectStore(STORE_MEMBERSHIPS);

    // `full` veut dire « le serveur ne sait pas où tu en étais » : on **remplace**
    // au lieu de fusionner. Fusionner sur un premier plein garderait indéfiniment
    // les objets supprimés pendant que le client était absent — leurs tombstones
    // ont pu expirer.
    if (page.full) {
      entities.clear();
      folders.clear();
      edges.clear();
      memberships.clear();
    }

    for (const { key, value } of page.entities) {
      if (value) entities.put(value, key);
      else entities.delete(key);
    }
    for (const { key, value } of page.folders) {
      if (value) folders.put(value, key);
      else folders.delete(key);
    }
    for (const { key, value } of page.memberships) {
      if (value) memberships.put(value, key);
      else memberships.delete(key);
    }
    for (const { key, value } of page.edges) {
      if (value) edges.put(value, key);
      else edges.delete(key);
    }

    // **Perdre l'appartenance efface le contenu du groupe** (D17). Le serveur
    // n'enverra plus rien de cette partition — il cesse de l'interroger dès que
    // l'appartenance quitte GSI1 —, donc aucun tombstone d'arête n'arrivera : sans
    // cette purge, le contenu révoqué resterait lisible hors ligne pour toujours.
    for (const gid of page.dropped) {
      const prefix = `GRP#${gid}|`;
      edges.openCursor().onsuccess = (ev) => {
        const cursor = (ev.target as IDBRequest<IDBCursorWithValue | null>).result;
        if (!cursor) return;
        if (String(cursor.key).startsWith(prefix)) cursor.delete();
        cursor.continue();
      };
    }

    tx.objectStore(STORE_META).put(page.cursor, KEY_CURSOR);
    await done(tx);
  }

  /**
   * Écrit le résultat d'une mutation locale.
   *
   * Le curseur **ne bouge pas** : ces octets-là viennent d'une réponse d'écriture,
   * pas d'une page de synchronisation. Les confondre ferait sauter au client tout
   * ce qui a changé ailleurs entre-temps.
   */
  async put(entities: readonly { key: string; value: Entity | null }[]): Promise<void> {
    const tx = this.db.transaction(STORE_ENTITIES, 'readwrite');
    const store = tx.objectStore(STORE_ENTITIES);
    for (const { key, value } of entities) {
      if (value) store.put(value, key);
      else store.delete(key);
    }
    await done(tx);
  }

  /** Remplace l'arborescence par celle que `GET /folders` vient de rendre. */
  async putFolders(list: readonly Folder[]): Promise<void> {
    const tx = this.db.transaction(STORE_FOLDERS, 'readwrite');
    const store = tx.objectStore(STORE_FOLDERS);
    store.clear();
    for (const f of list) store.put(f, f.folder);
    await done(tx);
  }

  /** À la déconnexion : le miroir d'un compte ne survit pas à sa session. */
  async clear(): Promise<void> {
    const tx = this.db.transaction(
      [STORE_ENTITIES, STORE_FOLDERS, STORE_EDGES, STORE_MEMBERSHIPS, STORE_META],
      'readwrite',
    );
    tx.objectStore(STORE_ENTITIES).clear();
    tx.objectStore(STORE_FOLDERS).clear();
    tx.objectStore(STORE_EDGES).clear();
    tx.objectStore(STORE_MEMBERSHIPS).clear();
    tx.objectStore(STORE_META).clear();
    await done(tx);
  }
}
